import { Close } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ESRState, Thread } from "../types/data";
type Props = {
	threads: Record<number, Thread> | null;
	loading: boolean;
	onChatClose: () => void;
};
export const FloatingMessageBox: FC<Props> = ({
	threads,
	loading,
	onChatClose,
}) => {
	const unreadMessageCount = useSelector(
		(state: ESRState) => state.unread_message_count,
	);
	const [searchESR, setSearchESR] = useState("");

	const filteredThreads = useMemo(() => {
		if (!threads) return [];

		const query = searchESR.trim();

		return Object.values(threads).filter((thread) => {
			if (thread.unread_messages <= 0) return false;
			if (!query) return true;

			const esr = String(thread.esr_id);
			const addendum = String(thread.addendum_id+""+thread.addendum_label || "");

			return esr.includes(query) || addendum.includes(query);
		});
	}, [threads, searchESR]);

	const navigate = useNavigate();

	function onNavigateToESR(thread: Thread) {
		const queryParam = `?thread_id=${thread.id}`;
		const esrId = thread.addendum_id || thread.esr_id;
		const activeLabel = thread.addendum_label || "initial";
		const activeId = thread.esr_id;
		if (thread?.question_id) {
			navigate(
				`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${thread.question_id}${queryParam}`,
				{ replace: true },
			);
		} else {
			navigate(`/esr/${esrId}/${activeLabel}/${activeId}/0${queryParam}`, {
				replace: true,
			});
		}
	}
	return (
		<>
			<Paper
				elevation={8}
				sx={{
					position: "fixed",
					bottom: 80,
					right: 16,
					width: 500,
					height: 400,
					zIndex: 4,
					display: "flex",
					flexDirection: "column",
					borderRadius: 3,
					overflow: "hidden",
				}}
			>
				<Box
					sx={{
						p: 2,
						bgcolor: "primary.main",
						color: "white",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Typography fontWeight={600}>Unread Messages</Typography>
						{unreadMessageCount !== 0 && (
							<Box
								sx={{
									bgcolor: "error.main",
									color: "white",
									borderRadius: "50%",
									width: 20,
									height: 20,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									fontSize: "0.75rem",
								}}
							>
								{unreadMessageCount}
							</Box>
						)}
					</Box>
					<IconButton
						size="small"
						onClick={onChatClose}
						sx={{ color: "white" }}
						aria-label="Close Chat"
					>
						<Close />
					</IconButton>
				</Box>
				{loading && (
					<>
						<Box
							sx={{
								height: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: "text.secondary",
								fontStyle: "italic",
							}}
						>
							Loading messages....
						</Box>
					</>
				)}
				{!loading && (
					<>
						<Box sx={{ p: 1.5, borderBottom: "1px solid #e0e0e0" }}>
							<TextField
								size="small"
								fullWidth
								placeholder="Search by ESR #"
								value={searchESR}
								onChange={(e) => setSearchESR(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon fontSize="small" />
										</InputAdornment>
									),
								}}
							/>
						</Box>

						<Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
							{threads &&
							Object.values(threads).length > 0 &&
							Object.values(threads).some(
								(thread) => thread.unread_messages > 0,
							) ? (
								<List dense>
                                    {/* Object.values(threads)
										.filter((thread) => thread.unread_messages > 0) */}
									{
                                        filteredThreads
										.map((thread) => (
											<ListItem
												onClick={() => {
													onChatClose();
													onNavigateToESR(thread);
												}}
												key={thread.id}
												sx={{
													mb: 1,
													p: 1.5,
													borderRadius: 2,
													border: "1px solid #e0e0e0",
													backgroundColor: "#fafafa",
													cursor: "pointer",
													position: "relative",
												}}
											>
												<Box>
													{thread.addendum_id.length > 0 &&
														thread.addendum_label.length > 0 && (
															<Typography variant="body2" fontWeight={600}>
																ESR #{thread.addendum_id}
																{thread.addendum_label}
															</Typography>
														)}
													{thread.addendum_id.length === 0 &&
														thread.addendum_label.length === 0 && (
															<Typography variant="body2" fontWeight={600}>
																ESR #{thread.esr_id}
															</Typography>
														)}
													<Box
														sx={{
															display: "inline",
															justifyContent: "flex-start",
														}}
													>
														<Typography
															variant="caption"
															color="text.secondary"
														>
															{thread.subject}
														</Typography>
														{thread.unread_messages > 0 && (
															<Typography fontSize={12} color="#F72A2A">
																{thread.unread_messages} unread message(s)
															</Typography>
														)}
													</Box>
												</Box>
											</ListItem>
										))}
								</List>
							) : (
								<Box
									sx={{
										height: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										color: "text.secondary",
										fontStyle: "italic",
									}}
								>
									No messages.
								</Box>
							)}
						</Box>
					</>
				)}
			</Paper>
		</>
	);
};
